import { useEffect, useMemo, useState } from "react";
import { Camera, ChevronLeft, ChevronRight, MessageCircleQuestion, NotebookPen, Send, Share2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { fmtDateTime, fmtDay } from "@/lib/format";
import { Btn, Field } from "@/components/rp";
import { Section } from "@/components/commesse";

const iso = (d) => d.toLocaleDateString("en-CA");
const WEEK = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const photoUrl = (e, p) => fileUrl(`/api/diario/${e.id}/photos/${p.id}`);

export const NewEntry = ({ oid, date, setDate, onAdded }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const fd = new FormData();
    fd.append("text", text); fd.append("date", date); fd.append("visible_to_client", visible);
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { onAdded((await api.post(`/orders/${oid}/diario`, fd)).data); setText(""); setFiles([]); toast.success("Giornata registrata"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl bg-[#F7F7F7] p-4 space-y-3" data-testid="diario-form">
      <div className="grid sm:grid-cols-[160px_1fr] gap-3">
        <Field label="Giorno"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rp-field bg-white" data-testid="diario-date" /></Field>
        <Field label="Cosa è stato fatto"><textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} className="rp-field bg-white" placeholder="Es. posati i pavimenti del bagno, completato l'impianto elettrico della cucina…" data-testid="diario-text" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <label className="rp-btn bg-white border border-[#EBEBEB] cursor-pointer h-9" data-testid="diario-photos"><Camera className="h-4 w-4" /> Foto ({files.length})
          <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => setFiles([...files, ...e.target.files].slice(0, 12))} data-testid="diario-photos-input" /></label>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} className="h-4 w-4 accent-black" data-testid="diario-visible" /> Visibile al cliente</label>
        <Btn className="ml-auto h-9" loading={busy} disabled={text.trim().length < 3} onClick={save} data-testid="diario-save"><NotebookPen className="h-4 w-4" /> Registra la giornata</Btn>
      </div>
    </div>
  );
};

export const Lightbox = ({ box, setBox }) => {
  useEffect(() => {
    if (!box) return undefined;
    const key = (ev) => {
      if (ev.key === "Escape") setBox(null);
      if (ev.key === "ArrowRight") setBox((b) => ({ ...b, i: (b.i + 1) % b.e.photos.length }));
      if (ev.key === "ArrowLeft") setBox((b) => ({ ...b, i: (b.i - 1 + b.e.photos.length) % b.e.photos.length }));
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [box, setBox]);
  if (!box) return null;
  const { e, i } = box;
  const n = e.photos.length;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setBox(null)} data-testid="diario-lightbox">
      <img src={photoUrl(e, e.photos[i])} alt={e.photos[i].filename} className="max-h-[88vh] max-w-[92vw] object-contain rounded-xl" onClick={(ev) => ev.stopPropagation()} />
      <button className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/15 text-white grid place-items-center" aria-label="Chiudi" data-testid="diario-lightbox-close"><X className="h-5 w-5" /></button>
      {n > 1 && <>
        <button onClick={(ev) => { ev.stopPropagation(); setBox({ e, i: (i - 1 + n) % n }); }} className="absolute left-4 h-11 w-11 rounded-full bg-white/15 text-white grid place-items-center" aria-label="Precedente" data-testid="diario-lightbox-prev"><ChevronLeft className="h-5 w-5" /></button>
        <button onClick={(ev) => { ev.stopPropagation(); setBox({ e, i: (i + 1) % n }); }} className="absolute right-4 h-11 w-11 rounded-full bg-white/15 text-white grid place-items-center" aria-label="Successiva" data-testid="diario-lightbox-next"><ChevronRight className="h-5 w-5" /></button>
      </>}
      <p className="absolute bottom-5 text-white/70 text-sm">{fmtDay(e.date)} · {i + 1} di {n}</p>
    </div>
  );
};

const Questions = ({ e, oid, admin, onSaved }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { onSaved((await api.post(`${admin ? "" : "/client"}/orders/${oid}/diario/${e.id}/questions`, { text })).data); setText(""); toast.success(admin ? "Risposta inviata al cliente" : "Domanda inviata allo staff"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (admin && !e.visible_to_client && !e.questions.length) return null;
  return (
    <div className="mt-4 space-y-2" data-testid={`diario-questions-${e.id}`}>
      {e.questions.map((q) => (
        <div key={q.id} className={`rounded-2xl px-4 py-2.5 text-sm max-w-[90%] ${q.role === "client" ? "bg-[#E6DDCE]/60" : "bg-[#F7F7F7] ml-auto"}`} data-testid="diario-question">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#757575]">{q.name} · {fmtDateTime(q.at)}</p>
          <p className="whitespace-pre-wrap">{q.text}</p>
        </div>
      ))}
      <div className="flex gap-2">
        <input value={text} onChange={(ev) => setText(ev.target.value)} className="rp-field bg-white h-10" placeholder={admin ? "Rispondi al cliente su questa giornata…" : "Fai una domanda su questa giornata…"} data-testid={`diario-question-input-${e.id}`} />
        <Btn className="h-10" loading={busy} disabled={text.trim().length < 2} onClick={send} data-testid={`diario-question-send-${e.id}`}>{admin ? <Send className="h-4 w-4" /> : <MessageCircleQuestion className="h-4 w-4" />} {admin ? "Rispondi" : "Chiedi"}</Btn>
      </div>
    </div>
  );
};

const ShareBtn = ({ e, oid, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try { onSaved((await api.post(`/orders/${oid}/diario/${e.id}/share`)).data); toast.success("Condiviso: il cliente riceve notifica ed email"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <button onClick={share} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-black text-white text-xs font-medium hover:bg-[#333] transition-colors disabled:opacity-60" data-testid={`diario-share-${e.id}`}>
      <Share2 className="h-3.5 w-3.5" /> {e.shared_at ? "Condividi di nuovo" : "Condividi col cliente"}
    </button>
  );
};

export const Entry = ({ e, oid, admin, onSaved, onRemove, setBox }) => (
  <div className="rounded-2xl border border-[#EBEBEB] bg-white p-4" data-testid={`diario-entry-${e.id}`}>
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#757575]">{e.author_name}{admin && !e.visible_to_client && " · solo staff"}{admin && e.shared_at && ` · condiviso ${fmtDateTime(e.shared_at)}`}</span>
      {admin && <ShareBtn e={e} oid={oid} onSaved={onSaved} />}
      {admin && <button onClick={() => onRemove(e)} className="text-[#757575] hover:text-[#DC3545]" aria-label="Elimina" data-testid={`diario-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button>}
    </div>
    <p className="text-sm mt-1 whitespace-pre-line">{e.text}</p>
    {e.photos.length > 0 && (
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
        {e.photos.map((p, i) => (
          <button key={p.id} onClick={() => setBox({ e, i })} className="block aspect-square overflow-hidden rounded-xl bg-[#F7F7F7]" data-testid={`diario-photo-${p.id}`}>
            <img src={photoUrl(e, p)} alt={p.filename} loading="lazy" className="h-full w-full object-cover hover:scale-105 transition-transform" />
          </button>
        ))}
      </div>
    )}
    <Questions e={e} oid={oid} admin={admin} onSaved={onSaved} />
  </div>
);

export const Calendar = ({ month, setMonth, byDay, day, setDay }) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const cells = Array.from({ length: (first.getDay() + 6) % 7 }, () => null)
    .concat(Array.from({ length: new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate() }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)));
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  return (
    <div data-testid="diario-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => shift(-1)} className="h-9 w-9 rounded-full border border-[#EBEBEB] grid place-items-center hover:border-black" aria-label="Mese precedente" data-testid="diario-prev-month"><ChevronLeft className="h-4 w-4" /></button>
        <p className="font-semibold capitalize" data-testid="diario-month">{month.toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</p>
        <button onClick={() => shift(1)} className="h-9 w-9 rounded-full border border-[#EBEBEB] grid place-items-center hover:border-black" aria-label="Mese successivo" data-testid="diario-next-month"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 text-center">
        {WEEK.map((w) => <span key={w} className="rp-label py-1">{w}</span>)}
        {cells.map((c, i) => {
          if (!c) return <span key={`x${i}`} />;
          const k = iso(c);
          const es = byDay[k] || [];
          const ph = es.find((e) => e.photos.length);
          const q = es.reduce((s, e) => s + e.questions.length, 0);
          return (
            <button key={k} onClick={() => setDay(k)} data-testid={`diario-day-${k}`}
              className={`relative aspect-square rounded-xl overflow-hidden text-sm transition-[box-shadow,background-color] ${day === k ? "ring-2 ring-black" : ""} ${es.length ? "bg-[#E6DDCE] font-semibold" : "bg-[#F7F7F7] text-[#757575] hover:bg-[#EBEBEB]"}`}>
              {ph && <img src={photoUrl(ph, ph.photos[0])} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-70" />}
              <span className={`absolute top-1 left-1.5 ${ph ? "text-white drop-shadow" : ""}`}>{c.getDate()}</span>
              {es.length > 0 && <span className="absolute bottom-1 right-1 rounded-full bg-black text-white text-[10px] px-1.5">{es.length}<span className="hidden sm:inline">{q ? ` · ${q}?` : ""}</span></span>}
              {k === iso(new Date()) && !es.length && <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-black" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const Diario = ({ oid, admin = false, bare = false }) => {
  const [list, setList] = useState(null);
  const [day, setDay] = useState(iso(new Date()));
  const [month, setMonth] = useState(new Date());
  const [box, setBox] = useState(null);
  useEffect(() => {
    api.get(admin ? `/orders/${oid}/diario` : `/client/orders/${oid}/diario`).then((r) => {
      setList(r.data);
      if (r.data.length && !admin) { const d = new Date(`${r.data[0].date}T12:00`); setDay(r.data[0].date); setMonth(d); }
    }).catch(() => setList([]));
  }, [oid, admin]);
  const byDay = useMemo(() => (list || []).reduce((m, e) => ({ ...m, [e.date]: [...(m[e.date] || []), e] }), {}), [list]);
  if (!list) return null;
  const replace = (e) => setList((l) => l.map((x) => (x.id === e.id ? e : x)));
  const remove = async (e) => {
    if (!window.confirm("Eliminare questa giornata del diario?")) return;
    try { await api.delete(`/orders/${oid}/diario/${e.id}`); setList((l) => l.filter((x) => x.id !== e.id)); } catch (err) { toast.error(errMsg(err)); }
  };
  const pick = (d) => { setDay(d); setMonth(new Date(`${d}T12:00`)); };
  const body = (
    <div className="grid lg:grid-cols-[minmax(280px,380px)_1fr] gap-6" data-testid="diario-section">
      <div>
        <Calendar month={month} setMonth={setMonth} byDay={byDay} day={day} setDay={setDay} />
        <p className="text-xs text-[#757575] mt-3">{list.length} giornat{list.length === 1 ? "a" : "e"} registrat{list.length === 1 ? "a" : "e"}{list.length > 0 && <> · ultima <button className="underline" onClick={() => pick(list[0].date)} data-testid="diario-last-day">{fmtDay(list[0].date)}</button></>}</p>
      </div>
      <div className="space-y-3" data-testid="diario-day-panel">
        <p className="font-semibold" data-testid="diario-selected-day">{fmtDay(day)}</p>
        {admin && <NewEntry oid={oid} date={day} setDate={pick} onAdded={(e) => setList((l) => [e, ...l].sort((a, b) => b.date.localeCompare(a.date)))} />}
        {!(byDay[day] || []).length && <p className="text-sm text-[#757575]">{admin ? "Nessuna attività registrata in questo giorno." : "Nessun aggiornamento in questo giorno: scegli un giorno evidenziato nel calendario."}</p>}
        {(byDay[day] || []).map((e) => <Entry key={e.id} e={e} oid={oid} admin={admin} onSaved={replace} onRemove={remove} setBox={setBox} />)}
      </div>
      <Lightbox box={box} setBox={setBox} />
    </div>
  );
  return bare ? body : <Section label="Diario di bordo" testid="diario-wrapper">{body}</Section>;
};
